import * as THREE from 'three';
import arena from './garageArena.json';
import { validateSurfaces, shapeOf } from '../core/surfaces.js';
import { createCrowdBodies } from './crowd.js';
import { createCar } from './garageCars.js';
import { arenaShape, buildLighting, createKit, drawnTexture } from './kit.js';

/**
 * A parking garage: one level of a multi-storey, low and lit by tubes.
 *
 * Everything hard is a shape the collision knew before this arena existed.
 * The square pillars are `box`es — a pillar with corners is the same thing as
 * a column with corners, which is what a box was added for — and so are the
 * two parked cars, which are the train's own shape standing still. The back
 * and end walls are `plane`s. The camera side is open into the next bay, the
 * way the subway's is open over the track.
 *
 * **The ceiling is the point of the room**, and it is why an arena's camera
 * bounds are now obeyed: at 2.75 m with beams below it, a camera left to back
 * off and climb ends up above the slab looking at the top of it. `maxY` 2.3
 * holds it under the beams and the pitch is flat to match.
 *
 * **The signature is a head into a car**: the bonnet, the windscreen, or the
 * door glass, depending on which face of the box a fighter is put into. The
 * cars take the damage where they took the body (see `garageCars.js`) and set
 * their alarms off, and a finish against one goes through the windscreen.
 * Nothing about the hit itself is new — it is an environmental hit off a hard
 * `box`, a finisher for anyone in danger, exactly as a pillar is. What is new
 * is that the arena hears about it: `onStruck`, subscribed by the host.
 */

export { arena as GARAGE_ARENA };

const WALL_THICKNESS = 0.25;

export function createGarageArena(config = arena, { onWarn, crowd = null } = {}) {
  try {
    validateSurfaces(config.surfaces);
  } catch (error) {
    onWarn?.(error.message);
  }

  const group = new THREE.Group();
  group.name = `arena-${config.id}`;
  const kit = createKit(group);
  const colors = config.palette ?? {};
  const tone = (key, fallback) => new THREE.Color(colors[key] ?? fallback);
  const slab = config.slab;
  const slabWidth = slab.maxX - slab.minX;
  const slabDepth = slab.maxZ - slab.minZ;
  const mid = { x: (slab.minX + slab.maxX) / 2, z: (slab.minZ + slab.maxZ) / 2 };
  const ceiling = config.ceilingHeight;

  // --- the floor: concrete, oil, and paint ------------------------------------
  const concrete = drawnTexture(512, 512, (ctx, w, h, random) => {
    ctx.fillStyle = colors.floor ?? '#5b5d5a';
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 9000; i++) {
      ctx.fillStyle = random() < 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.05)';
      ctx.fillRect(random() * w, random() * h, 1 + random() * 2, 1 + random() * 2);
    }
    // Oil where cars have stood, soft-edged.
    for (let i = 0; i < 7; i++) {
      const x = random() * w;
      const y = random() * h;
      const r = 20 + random() * 50;
      const stain = ctx.createRadialGradient(x, y, 0, x, y, r);
      stain.addColorStop(0, 'rgba(20,20,18,0.42)');
      stain.addColorStop(1, 'rgba(20,20,18,0)');
      ctx.fillStyle = stain;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    // Saw cuts in the slab.
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, w, h);
  }, { seed: 21, repeat: [slabWidth / 4, slabDepth / 4] });
  if (concrete) kit.keep(concrete);
  kit.box(slabWidth, slab.thickness, slabDepth, kit.material({
    color: concrete ? 0xffffff : tone('floor', '#5b5d5a'),
    map: concrete,
    roughness: 0.9,
    metalness: 0.02,
  }), { x: mid.x, y: -slab.thickness / 2, z: mid.z, name: 'garage-floor', casts: false });

  const paint = kit.material({ color: tone('line', '#d8c25a'), roughness: 0.7, emissive: tone('line', '#d8c25a'), emissiveIntensity: 0.05 });
  const bays = config.bays;
  for (const x of bays.lines) {
    kit.box(bays.width, 0.006, bays.to - bays.from, paint, { x, y: 0.004, z: (bays.from + bays.to) / 2, name: 'bay-line', casts: false });
  }
  // The aisle line along the front of the bays.
  kit.box(slabWidth - 1, 0.006, bays.width, paint, { x: mid.x, y: 0.004, z: bays.to + 0.25, name: 'aisle-line', casts: false });

  // --- the ceiling, beams, tubes and a sprinkler main ------------------------
  const ceilingMaterial = kit.material({ color: tone('ceiling', '#6a6c68'), roughness: 0.95 });
  kit.box(slabWidth, 0.3, slabDepth, ceilingMaterial, {
    x: mid.x,
    y: ceiling + 0.15,
    z: mid.z,
    name: 'ceiling',
    casts: false,
  });
  const beam = kit.material({ color: tone('beam', '#73756f'), roughness: 0.92 });
  const beams = config.beams;
  for (let z = slab.minZ + beams.every / 2; z < slab.maxZ; z += beams.every) {
    kit.box(slabWidth, beams.depth, beams.width, beam, {
      x: mid.x,
      y: ceiling - beams.depth / 2,
      z,
      name: 'ceiling-beam',
      casts: false,
    });
  }
  const pipeMaterial = kit.material({ color: '#a8352c', roughness: 0.5, metalness: 0.4 });
  kit.cylinder(0.05, slabWidth, pipeMaterial, { x: mid.x, y: ceiling - beams.depth - 0.08, z: -1.9, rotZ: Math.PI / 2, segments: 10, name: 'sprinkler-pipe', casts: false });
  const tubeMaterial = kit.material({ color: tone('tube', '#e8fff4'), emissive: tone('tube', '#e8fff4'), emissiveIntensity: 2.4 });
  const housing = kit.material({ color: '#c9cdc8', roughness: 0.6 });
  for (const tube of config.tubes ?? []) {
    kit.box(tube.length + 0.08, 0.05, 0.2, housing, { x: tube.x, y: ceiling - beams.depth - 0.03, z: tube.z, name: 'tube-housing', casts: false });
    kit.box(tube.length, 0.04, 0.08, tubeMaterial, { x: tube.x, y: ceiling - beams.depth - 0.07, z: tube.z, name: 'tube', casts: false });
  }

  // --- the walls, the pillars, the cars --------------------------------------
  const wallMaterial = kit.material({ color: tone('wall', '#7b7d77'), roughness: 0.93 });
  const stripe = kit.material({ color: tone('hazard', '#d9b23a'), roughness: 0.6 });
  const pillarFace = pillarTexture(colors);
  if (pillarFace) kit.keep(pillarFace);
  const pillarMaterial = kit.material({
    color: pillarFace ? 0xffffff : tone('pillar', '#8a8c86'),
    map: pillarFace,
    roughness: 0.9,
  });

  const cars = new Map();
  const built = [];
  for (const surface of config.surfaces) {
    if (surface.look === 'car') {
      const car = createCar(kit, surface, {
        paint: config.cars?.[surface.id]?.body,
        palette: colors,
        damage: config.damage,
      });
      group.add(car.object3D);
      cars.set(surface.id, car);
      built.push({ surface, mesh: car.object3D });
      continue;
    }
    if (shapeOf(surface) === 'box') {
      const { center, halfX, halfZ } = surface;
      const mesh = kit.box(halfX * 2, ceiling, halfZ * 2, pillarMaterial, {
        x: center.x,
        y: ceiling / 2,
        z: center.z,
        name: `pillar-${surface.id}`,
      });
      built.push({ surface, mesh });
      continue;
    }
    const { from, to, normal } = surface;
    const span = Math.hypot(to.x - from.x, to.z - from.z) + 0.3;
    const rotY = Math.atan2(-normal.x, -normal.z);
    const at = { x: (from.x + to.x) / 2 - normal.x * (WALL_THICKNESS / 2), z: (from.z + to.z) / 2 - normal.z * (WALL_THICKNESS / 2) };
    const mesh = kit.box(span, ceiling, WALL_THICKNESS, wallMaterial, { ...at, y: ceiling / 2, rotY, name: `wall-${surface.id}` });
    // A painted band at bumper height, which every garage has and which says
    // how low the room is better than the ceiling does.
    kit.box(span, 0.22, 0.01, stripe, {
      x: at.x + normal.x * (WALL_THICKNESS / 2 + 0.006),
      y: 0.55,
      z: at.z + normal.z * (WALL_THICKNESS / 2 + 0.006),
      rotY,
      name: `wall-band-${surface.id}`,
      casts: false,
    });
    built.push({ surface, mesh });
  }

  // An exit sign on the east wall, for colour and for scale.
  kit.box(0.5, 0.18, 0.04, kit.material({ color: '#1f9b52', emissive: '#1f9b52', emissiveIntensity: 1.6 }), {
    x: 8.6 - 0.04,
    y: 2.05,
    z: 1.2,
    rotY: Math.PI / 2,
    name: 'exit-sign',
    casts: false,
  });

  // Everything that never moves or changes, one draw call per material. The
  // bonnets and the glass stay their own meshes: one dents, the other breaks.
  kit.mergeStatic(group, { exclude: (mesh) => /car-hood|car-windscreen|car-rear-window|car-window/.test(mesh.name) });

  group.add(buildLighting(config.lighting));

  const bodies =
    crowd && config.crowd
      ? createCrowdBodies(crowd, { config: config.crowd, surfaces: config.surfaces, palette: colors })
      : null;
  if (bodies) group.add(bodies.object3D);

  return arenaShape(config, {
    group,
    kit,
    extra: { crowd: bodies, walls: built, cars },
    update(dt) {
      bodies?.update(dt);
      for (const car of cars.values()) car.update(dt);
    },
    dispose() {
      bodies?.dispose();
    },
    reset() {
      for (const car of cars.values()) car.reset();
    },
    onStruck(event) {
      cars.get(event.surface)?.hit(event);
      return null;
    },
  });
}

/** Painted concrete with a hazard band at the foot and the level number. */
function pillarTexture(colors) {
  return drawnTexture(128, 512, (ctx, w, h, random) => {
    ctx.fillStyle = colors.pillar ?? '#8a8c86';
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 1500; i++) {
      ctx.fillStyle = random() < 0.5 ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)';
      ctx.fillRect(random() * w, random() * h, 2, 2);
    }
    // Black and yellow chevrons up to knee height, where bumpers hit.
    const band = h * 0.16;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, h - band, w, band);
    ctx.clip();
    ctx.fillStyle = colors.hazard ?? '#d9b23a';
    ctx.fillRect(0, h - band, w, band);
    ctx.fillStyle = '#1b1b1b';
    for (let x = -h; x < w + h; x += 36) {
      ctx.beginPath();
      ctx.moveTo(x, h);
      ctx.lineTo(x + 18, h);
      ctx.lineTo(x + 18 + band, h - band);
      ctx.lineTo(x + band, h - band);
      ctx.fill();
    }
    ctx.restore();
    ctx.fillStyle = '#e8e2c8';
    ctx.font = 'bold 64px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('P2', w / 2, h * 0.42);
  }, { seed: 31 });
}
